import { useEffect, useState } from "react";

import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";

import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";
import { generateUUID } from "../../services/AbstractService";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionTitle from "../../components/AccordionTitle";

import { doService } from "../../services/ServiceHelper";
import { getBulkDelete, deleteSADs } from "../../services/ActivityService";
import StudentSelector from "../../components/faculty/report/StudentSelector";
import MyActivitySelector from "../../components/faculty/report/MyActivitySelector";
import PeriodSelector from "../../components/faculty/report/PeriodSelector";
import { showDialog, showOKCancelDialog } from "../../components/ATPDialogs";
import { getPeriodName } from "../../services/SchoolService";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";

const BulkDelete = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_searchPerformed, setSearchPerformed] = useState(false);

  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);

  const [_results, setResults] = useState([]);
  const [_selectedStudentEid, setSelectedStudentEid] = useState("XX");
  const [_selectedActivityEid, setSelectedActivityEid] = useState("XX");
  const [_selectedPeriod, setSelectedPeriod] = useState("1");
  const [_selectedSortBy, setSelectedSortBy] = useState("DD");
  const [_selectAll, setSelectAll] = useState(false);

  useEffect(() => {
  }, []);

  const handleChangeDateRange = (value) => setDateRange(value);

  const handleSearch = () => {
    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    setShowProgress(true);
    //startDate, endDate, studentEid, studentListEid, activityEid, period, orderBy
    doService(
      getBulkDelete(
        _school,
        _jwt,
        dateStart,
        dateEnd,
        _selectedStudentEid,
        "XX",
        _selectedActivityEid,
        _selectedPeriod,
        _selectedSortBy
      ),
      (results) => {
        setSearchPerformed(true);

        // create attribute to capture if row clicked
        for (let result of results.data) {
          result.checked = false;
        }

        setResults(results.data);
        console.log(results.data);
        setShowProgress(false);
      }
    );
  };

  const onSelectStudent = (value) => setSelectedStudentEid(value);

  const onChangeActivityEid = (activityEid) => {
    setSelectedActivityEid(activityEid);
  };
  const onChangePeriod = (period) => {
    setSelectedPeriod(period);
  };

  const handleChangeSortBy = (event) => setSelectedSortBy(event.target.value);

  const handleChangeSelectAll = (event) => {
    setSelectAll(event.target.checked);

    // update selected checkboxes
    const newResults = cloneDeep(_results);
    for (let result of newResults) {
      result.checked = event.target.checked;
    }

    setResults(newResults);
  };

  const handleClickDelete = () => {
    // get clicked items
    const toDeleteEids = [];
    for (let result of _results) {
      if (result.checked) {
        toDeleteEids.push(result.eid);
      }
    }

    if (toDeleteEids.length === 0) {
      showDialog(
        "Unable to Delete",
        "Please select 1 or more student activities."
      );
      return;
    }

    showOKCancelDialog(
      "Confirm Delete",
      "WARNING: You are about to PERMANENTLY delete from student calendars.  Click OK to proceed.",
      () => {
        setShowProgress(true);
        doService(deleteSADs(_school, _jwt, toDeleteEids), (results) => {
          handleSearch();
          window.showSnack("Signups Deleted.");
          setShowProgress(false);
        });
      }
    );
  };

  const handleChangeSelect = (event, result) => {
    const results = [..._results];

    for (let r of results) {
      if (r.eid === result.eid) {
        r.checked = event.target.checked;
      }
    }

    setResults(results);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Bulk Delete Student Signups" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Alert severity="info" sx={{ pb: 0, mt: 1 }}>
          Use the Search Criteria section below to find the student activity
          entries. Click the Search button, use the checkbox to select the
          entries, and click the Delete button to remove them.
        </Alert>

        <Alert severity="error" sx={{ pb: 0, mt: 1 }}>
          If you delete activities from the student schedules, they will be
          PERMANENTLY REMOVED from the system.
        </Alert>

        <Accordion defaultExpanded sx={{ mt: 1 }}>
          <AccordionSummary className="accordionHeader">
            <AccordionTitle text="Search Criteria" />
          </AccordionSummary>
          <AccordionDetails>
            <InputLabel>Start and End Dates *:</InputLabel>

            <MobileDateRangePicker
              defaultValue={[dayjs(), dayjs()]}
              value={_dateRange}
              sx={{ mt: 1, mb: 2 }}
              onChange={handleChangeDateRange}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputLabel>Select Student *:</InputLabel>
                <StudentSelector
                  school={_school}
                  jwt={_jwt}
                  onChange={onSelectStudent}
                />
              </Grid>
              <Grid item xs={6} sx={{ mt: 0, pt: 0 }}>
                <MyActivitySelector
                  school={_school}
                  jwt={_jwt}
                  onChange={onChangeActivityEid}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0.0 }}>
              <Grid item xs={6}>
                <PeriodSelector
                  school={_school}
                  onChangePeriod={onChangePeriod}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>Sort By *:</InputLabel>
                <Select
                  fullWidth
                  size="small"
                  onChange={handleChangeSortBy}
                  value={_selectedSortBy}
                >
                  <MenuItem key="DD" value="DD">
                    Date (descending)
                  </MenuItem>
                  <MenuItem key="DA" value="DA">
                    Date (ascending)
                  </MenuItem>
                  <MenuItem key="S" value="S">
                    Student
                  </MenuItem>
                  <MenuItem key="A" value="A">
                    Activity
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>

            <Button
              sx={{ mt: 1 }}
              variant="contained"
              fullWidth
              onClick={handleSearch}
            >
              Search
            </Button>
          </AccordionDetails>
        </Accordion>

        {_searchPerformed && (
          <Accordion defaultExpanded sx={{ mt: 1 }}>
            <AccordionSummary className="accordionHeader">
              <AccordionTitle
                text={`Student Activities (${_results.length})`}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} sx={{ mt: 0.0 }}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(event) => handleChangeSelectAll(event)}
                        checked={_selectAll}
                      />
                    }
                    label={"Select/Deselect All"}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Button
                    sx={{ mt: 1 }}
                    variant="outlined"
                    fullWidth
                    onClick={handleClickDelete}
                  >
                    Delete Signups
                  </Button>
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
                <Table size="small">
                  <TableHead>
                    {_selectedSortBy === "DD" && (
                      <StripedTableRow>
                        <StripedTableCell>Date</StripedTableCell>
                        <StripedTableCell>Activity Name</StripedTableCell>
                        <StripedTableCell align="center">
                          Period
                        </StripedTableCell>
                        <StripedTableCell>Student</StripedTableCell>
                      </StripedTableRow>
                    )}
                    {_selectedSortBy === "DA" && (
                      <StripedTableRow>
                        <StripedTableCell>Date</StripedTableCell>
                        <StripedTableCell>Activity Name</StripedTableCell>
                        <StripedTableCell align="center">
                          Period
                        </StripedTableCell>
                        <StripedTableCell>Student</StripedTableCell>
                      </StripedTableRow>
                    )}
                    {_selectedSortBy === "S" && (
                      <StripedTableRow>
                        <StripedTableCell>Student</StripedTableCell>
                        <StripedTableCell>Date</StripedTableCell>
                        <StripedTableCell>Activity Name</StripedTableCell>
                        <StripedTableCell align="center">
                          Period
                        </StripedTableCell>
                      </StripedTableRow>
                    )}
                    {_selectedSortBy === "A" && (
                      <StripedTableRow>
                        <StripedTableCell>Activity Name</StripedTableCell>
                        <StripedTableCell>Date</StripedTableCell>
                        <StripedTableCell align="center">
                          Period
                        </StripedTableCell>
                        <StripedTableCell>Student</StripedTableCell>
                      </StripedTableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {_results.length === 0 && (
                      <StripedTableRow>
                        <StripedTableCell colSpan={10}>
                          -- NO RESULTS RETURNED --
                        </StripedTableCell>
                      </StripedTableRow>
                    )}

                    {_results.map((result) => (
                      <>
                        {_selectedSortBy === "DD" && (
                          <StripedTableRow key={generateUUID()}>
                            <StripedTableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onChange={(event) =>
                                      handleChangeSelect(event, result)
                                    }
                                    checked={result.checked}
                                  />
                                }
                                label={dayjs(result.date).format("MM/DD")}
                              />
                            </StripedTableCell>
                            <StripedTableCell>
                              {result.activityName}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {getPeriodName(_school, result.period-1)}
                            </StripedTableCell>
                            <StripedTableCell>
                              {result.last_name}, {result.first_name}
                            </StripedTableCell>
                          </StripedTableRow>
                        )}
                        {_selectedSortBy === "DA" && (
                          <StripedTableRow key={generateUUID()}>
                            <StripedTableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onChange={(event) =>
                                      handleChangeSelect(event, result)
                                    }
                                    checked={result.checked}
                                  />
                                }
                                label={dayjs(result.date).format("MM/DD")}
                              />
                            </StripedTableCell>
                            <StripedTableCell>
                              {result.activityName}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {result.period}
                            </StripedTableCell>
                            <StripedTableCell>
                              {result.last_name}, {result.first_name}
                            </StripedTableCell>
                          </StripedTableRow>
                        )}
                        {_selectedSortBy === "S" && (
                          <StripedTableRow key={generateUUID()}>
                            <StripedTableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onChange={(event) =>
                                      handleChangeSelect(event, result)
                                    }
                                    checked={result.checked}
                                  />
                                }
                                label={
                                  result.last_name + ", " + result.first_name
                                }
                              />
                            </StripedTableCell>
                            <StripedTableCell>
                              {dayjs(result.date).format("MM/DD")}
                            </StripedTableCell>
                            <StripedTableCell>
                              {result.activityName}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {result.period}
                            </StripedTableCell>
                          </StripedTableRow>
                        )}
                        {_selectedSortBy === "A" && (
                          <StripedTableRow key={generateUUID()}>
                            <StripedTableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onChange={(event) =>
                                      handleChangeSelect(event, result)
                                    }
                                    checked={result.checked}
                                  />
                                }
                                label={result.activityName}
                              />
                            </StripedTableCell>
                            <StripedTableCell>
                              {dayjs(result.date).format("MM/DD")}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {result.period}
                            </StripedTableCell>
                            <StripedTableCell>
                              {result.last_name}, {result.first_name}
                            </StripedTableCell>
                          </StripedTableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}
      </Container>
    </>
  );
};

export default BulkDelete;
